import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Wifi as WifiIcon,
  LocationOn as LocationIcon,
  AutoFixHigh as AutoSuggestIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { unifiApi, locationsApi } from '../../../services/apiClient';

const UniFiSiteMappingDialog = ({ open, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [locations, setLocations] = useState([]);
  const [mappings, setMappings] = useState({}); // { unifiSiteId: locationId }
  const [syncingSite, setSyncingSite] = useState(null);

  const fetchData = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const [suggestionsRes, locationsRes] = await Promise.all([
        unifiApi.getSiteMappingSuggestions(),
        locationsApi.getAll()
      ]);

      if (suggestionsRes.data.success) {
        setSuggestions(suggestionsRes.data.suggestions);

        // Initialize mappings from existing + suggested
        const initialMappings = {};
        suggestionsRes.data.suggestions.forEach(s => {
          if (s.currentMapping?.locationId) {
            initialMappings[s.unifiSiteId] = s.currentMapping.locationId;
          }
        });
        setMappings(initialMappings);
      }

      if (locationsRes.data) {
        const locs = locationsRes.data.locations || locationsRes.data.data || locationsRes.data;
        const siteLocations = (Array.isArray(locs) ? locs : []).filter(l => l.locationType === 1 || l.location_type === 1);
        siteLocations.sort((a, b) => (a.friendlyName || '').localeCompare(b.friendlyName || ''));
        setLocations(siteLocations);
      }
    } catch (error) {
      enqueueSnackbar('Failed to load site mapping data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [open, enqueueSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAutoSuggest = () => {
    const newMappings = { ...mappings };
    suggestions.forEach(s => {
      if (!newMappings[s.unifiSiteId] && s.suggestedLocation) {
        newMappings[s.unifiSiteId] = s.suggestedLocation.id;
      }
    });
    setMappings(newMappings);
    enqueueSnackbar('Auto-suggestions applied', { variant: 'info' });
  };

  const handleMappingChange = (unifiSiteId, locationId) => {
    setMappings(prev => ({
      ...prev,
      [unifiSiteId]: locationId || null
    }));
  };

  const handleSaveAll = async () => {
    setSaving(true);
    let saved = 0;
    let failed = 0;

    for (const suggestion of suggestions) {
      const locationId = mappings[suggestion.unifiSiteId];

      // Only save if there's a mapping or if we need to update
      if (locationId || suggestion.currentMapping) {
        try {
          await unifiApi.createSiteMapping({
            unifiSiteId: suggestion.unifiSiteId,
            unifiSiteName: suggestion.unifiSiteName,
            internalReference: suggestion.internalReference,
            locationId: locationId || null
          });
          saved++;
        } catch (error) {
          failed++;
        }
      }
    }

    setSaving(false);

    if (failed === 0) {
      enqueueSnackbar(`${saved} site mapping${saved !== 1 ? 's' : ''} saved`, { variant: 'success' });
    } else {
      enqueueSnackbar(`${saved} saved, ${failed} failed`, { variant: 'warning' });
    }

    fetchData(); // Refresh
  };

  const handleDeleteMapping = async (suggestion) => {
    if (!suggestion.currentMapping?.id) return;

    try {
      await unifiApi.deleteSiteMapping(suggestion.currentMapping.id);
      setMappings(prev => {
        const next = { ...prev };
        delete next[suggestion.unifiSiteId];
        return next;
      });
      enqueueSnackbar('Mapping removed', { variant: 'success' });
      fetchData();
    } catch (error) {
      enqueueSnackbar('Failed to remove mapping', { variant: 'error' });
    }
  };

  const handleSyncSite = async (siteId, siteName) => {
    setSyncingSite(siteId);
    try {
      const res = await unifiApi.syncSite(siteId);
      const d = res.data?.devices || {};
      const c = res.data?.clients || {};
      enqueueSnackbar(
        `Synced "${siteName}" — Devices: ${d.created || 0} new, ${d.updated || 0} updated, ${d.failed || 0} failed | Clients: ${c.created || 0} new, ${c.updated || 0} updated`,
        { variant: d.failed ? 'warning' : 'success' }
      );
    } catch (error) {
      enqueueSnackbar(`Failed to sync "${siteName}"`, { variant: 'error' });
    } finally {
      setSyncingSite(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WifiIcon sx={{ mr: 1, color: 'info.main' }} />
            <Typography variant="h6">UniFi Site-to-Location Mapping</Typography>
          </Box>
          <Box>
            <Tooltip title="Auto-suggest mappings based on name similarity">
              <Button
                startIcon={<AutoSuggestIcon />}
                onClick={handleAutoSuggest}
                size="small"
                sx={{ mr: 1 }}
              >
                Auto-Suggest
              </Button>
            </Tooltip>
            <Tooltip title="Refresh sites from UniFi">
              <IconButton onClick={fetchData} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : suggestions.length === 0 ? (
          <Alert severity="warning">
            No UniFi sites found. Verify your API key configuration.
          </Alert>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Map UniFi sites to CARO locations. Mapped sites will auto-sync hourly and devices will be linked to the correct location.
            </Alert>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>UniFi Site</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Reference</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 250 }}>CARO Location</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Suggestion</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {suggestions.map((suggestion) => (
                    <TableRow key={suggestion.unifiSiteId} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {suggestion.unifiSiteName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {suggestion.unifiSiteId.slice(0, 8)}...
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {suggestion.internalReference || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <FormControl fullWidth size="small">
                          <Select
                            value={mappings[suggestion.unifiSiteId] || ''}
                            onChange={(e) => handleMappingChange(suggestion.unifiSiteId, e.target.value)}
                            displayEmpty
                          >
                            <MenuItem value="">
                              <em>Not mapped</em>
                            </MenuItem>
                            {locations.map(loc => (
                              <MenuItem key={loc.id} value={loc.id}>
                                {loc.friendlyName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        {suggestion.suggestedLocation && !mappings[suggestion.unifiSiteId] && (
                          <Chip
                            label={`${suggestion.suggestedLocation.name} (${suggestion.suggestedLocation.confidence}%)`}
                            size="small"
                            color="info"
                            variant="outlined"
                            onClick={() => handleMappingChange(suggestion.unifiSiteId, suggestion.suggestedLocation.id)}
                            sx={{ cursor: 'pointer' }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Sync this site now">
                          <span>
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleSyncSite(suggestion.unifiSiteId, suggestion.unifiSiteName)}
                              disabled={syncingSite === suggestion.unifiSiteId}
                            >
                              {syncingSite === suggestion.unifiSiteId
                                ? <CircularProgress size={18} />
                                : <SyncIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        {suggestion.currentMapping && (
                          <Tooltip title="Remove mapping">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteMapping(suggestion)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="info"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSaveAll}
          disabled={saving || loading}
        >
          {saving ? 'Saving...' : 'Save All Mappings'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UniFiSiteMappingDialog;
